import React from 'react';
import PropTypes from 'prop-types';
import { Quote } from '../../../quotes/quote';
import ReviewQuoteProduct from './ReviewQuoteProduct';
import { QuoteProductConfiguration } from '../../../quotes/quoteProductConfiguration';
import { ProductConfiguration } from '../../../products/productConfiguration';
import { FormDropdown as Dropdown } from '@ami/react/core';
import classNames from 'classnames';
import { priceModeOptions } from '../../../quotes/quotePriceMode';
import ReviewQuoteProductCustomizationModal from './ReviewQuoteProductCustomizationModal';
import ReviewQuoteProductOverridePricingModal from './ReviewQuoteProductOverridePricingModal';
import { ProductOptionSearchItem } from '../../../products/productOption';
import ReviewQuoteProductOverrideDiscountModal from './ReviewQuoteProductOverrideDiscountModal';
import { PricingAndLeadTimesBanner } from '../../PricingAndLeadTimesBanner';

const AddMoreProducts = ({ onClick }) => (
  <div className="row justify-content-center">
    <button type="button" className="btn btn-outline-primary" onClick={onClick}>
      Add product
    </button>
  </div>
);

const Table = ({
  show,
  quote,
  quoteProductConfiguration,
  onQuantityUpdated,
  onEdit,
  onRemove,
  onAddCustomization,
  onRemoveCustomization,
  onOverridePricing,
  onAddOverrideDiscount,
  onEditOverrideDiscount,
  onRemoveOverrideDiscount,
  productConfiguration,
  msrpClassNames,
  dNetClassNames,
  selectedPriceMode,
  productOptions,
  isEditMode
}) => {
  if (!show) {
    return null;
  }
  return (
    <table className="table review-product-table table-mobile">
      <thead>
        <tr>
          <th width="12%"></th>
          <th width="32%" className="text-start">
            Details
          </th>
          <th width="12%" className="text-center">
            Lead Time
          </th>
          <th width="44%">
            <div className="row">
              <div className="col-6"></div>
              <div className={classNames(msrpClassNames, 'text-end')}>MSRP</div>
              <div className={classNames(dNetClassNames, 'text-end')}>
                D. NET
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {quote.products.map((quoteProduct) => (
          <ReviewQuoteProduct
            key={quoteProduct.id}
            quote={quote}
            quoteProduct={quoteProduct}
            quoteProductConfiguration={quoteProductConfiguration}
            onQuantityUpdated={(quantity) =>
              onQuantityUpdated(quoteProduct.id, quantity)
            }
            onEdit={onEdit}
            onRemove={onRemove}
            onAddCustomization={onAddCustomization}
            onRemoveCustomization={onRemoveCustomization}
            onOverridePricing={onOverridePricing}
            onAddOverrideDiscount={onAddOverrideDiscount}
            onEditOverrideDiscount={onEditOverrideDiscount}
            onRemoveOverrideDiscount={onRemoveOverrideDiscount}
            productConfiguration={productConfiguration}
            msrpClassNames={msrpClassNames}
            dNetClassNames={dNetClassNames}
            selectedPriceMode={selectedPriceMode}
            productOptions={productOptions}
            isEditMode={isEditMode}
          />
        ))}
      </tbody>
    </table>
  );
};

export default class ReviewQuoteProducts extends React.PureComponent {
  constructor(props) {
    super(props);
    this.customizationModalRef = React.createRef();
    this.overridePricingModalRef = React.createRef();
    this.overrideDiscountModalRef = React.createRef();
  }

  static propTypes = {
    quote: PropTypes.instanceOf(Quote).isRequired,
    quoteProductConfiguration: PropTypes.instanceOf(QuoteProductConfiguration)
      .isRequired,
    onQuantityUpdated: PropTypes.func.isRequired,
    onAddProduct: PropTypes.func.isRequired,
    onEditProduct: PropTypes.func.isRequired,
    onRemoveProduct: PropTypes.func.isRequired,
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    selectedPriceMode: PropTypes.string.isRequired,
    updatePriceMode: PropTypes.func.isRequired,
    onAddProductCustomization: PropTypes.func.isRequired,
    onAddOverrideDiscount: PropTypes.func.isRequired,
    onEditOverrideDiscount: PropTypes.func.isRequired,
    onRemoveOverrideDiscount: PropTypes.func.isRequired,
    onRemoveProductCustomization: PropTypes.func.isRequired,
    onUpdateProduct: PropTypes.func.isRequired,
    msrpClassNames: PropTypes.string.isRequired,
    dNetClassNames: PropTypes.string.isRequired,
    productOptions: PropTypes.arrayOf(
      PropTypes.instanceOf(ProductOptionSearchItem)
    ).isRequired,
    onLeadTimeOverrideUpdated: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool.isRequired,
  };

  render() {
    const {
      quote,
      quoteProductConfiguration,
      onQuantityUpdated,
      onEditProduct,
      onAddProduct,
      onRemoveProduct,
      productConfiguration,
      selectedPriceMode,
      updatePriceMode,
      onAddProductCustomization,
      onRemoveProductCustomization,
      onAddOverrideDiscount,
      onEditOverrideDiscount,
      onRemoveOverrideDiscount,
      onUpdateProduct,
      msrpClassNames,
      dNetClassNames,
      productOptions,
      onLeadTimeOverrideUpdated,
      isEditMode
    } = this.props;
    return (
      <React.Fragment>
        <div className="card-deck">
          <div className="card mb-4 box-shadow">
            <div className="card-body">
              <div className="row">
                <div className="offset-md-3 col-md-6">
                  <h6 className="card-title text-center">Products</h6>
                </div>
                {(quote.products.length > 0 ||
                  quote.customLineItems.length > 0) && (
                  <div className="col-md-3">
                    <Dropdown
                      placeholder="Pick a price mode"
                      label=""
                      items={Object.keys(priceModeOptions).map((x) => ({
                        id: x,
                        label: priceModeOptions[x],
                      }))}
                      selectedId={selectedPriceMode}
                      onChange={(selectedValue) =>
                        updatePriceMode(selectedValue)
                      }
                    />
                  </div>
                )}
              </div>
               { quote.maskPricingAndLeadTimes  &&  <PricingAndLeadTimesBanner /> }
              <Table
                show={quote.products.length > 0}
                quote={quote}
                quoteProductConfiguration={quoteProductConfiguration}
                onQuantityUpdated={onQuantityUpdated}
                onEdit={onEditProduct}
                onRemove={onRemoveProduct}
                onAddCustomization={(quoteProductId) =>
                  this.customizationModalRef.current.addCustomizationFor(
                    quoteProductId
                  )
                }
                onRemoveCustomization={onRemoveProductCustomization}
                onOverridePricing={(quoteProductId) => {
                  const quoteProduct = quote.products.find(x => x.id === quoteProductId);
                  this.overridePricingModalRef.current.overridePricingFor(
                    quoteProduct
                  );
                }}
                onAddOverrideDiscount={(quoteProductId) => {
                  this.overrideDiscountModalRef.current.addOverrideDiscountFor(
                    quoteProductId
                  )
                }}
                onEditOverrideDiscount={(quoteProductId) => {
                  const quoteProduct = quote.products.find((x) => x.id === quoteProductId);
                  this.overrideDiscountModalRef.current.editOverrideDiscountFor(
                    quoteProductId,
                    quoteProduct.userDefinedDiscount
                  )
                }}
                onRemoveOverrideDiscount={onRemoveOverrideDiscount}
                productConfiguration={productConfiguration}
                msrpClassNames={msrpClassNames}
                dNetClassNames={dNetClassNames}
                selectedPriceMode={selectedPriceMode}
                productOptions={productOptions}
                onLeadTimeOverrideUpdated={onLeadTimeOverrideUpdated}
                isEditMode={isEditMode}
              />
              <AddMoreProducts onClick={onAddProduct} />
            </div>
          </div>
        </div>
        <ReviewQuoteProductCustomizationModal
          ref={this.customizationModalRef}
          onCustomizationCreated={onAddProductCustomization}
        />
        <ReviewQuoteProductOverridePricingModal
          ref={this.overridePricingModalRef}
          onUpdateProduct={onUpdateProduct}
          productOptions={productOptions}
          msrpMarkupPercentage={
            quote.step1.dealerBranchSummary.msrpMarkupPercentage
          }
        />
        <ReviewQuoteProductOverrideDiscountModal
          ref={this.overrideDiscountModalRef}
          onOverrideDiscountCreated={onAddOverrideDiscount}
          onOverrideDiscountUpdated={onEditOverrideDiscount}
        />
      </React.Fragment>
    );
  }
}
